import { create } from 'zustand';

type EventCallback = (data: Record<string, unknown>) => void;

interface SSEState {
  connected: boolean;
  listeners: Map<string, Set<EventCallback>>;
  eventSource: EventSource | null;
  connect: (sellerId?: number) => void;
  disconnect: () => void;
  subscribe: (event: string, callback: EventCallback) => () => void;
}

export const useSSEStore = create<SSEState>((set, get) => ({
  connected: false,
  listeners: new Map(),
  eventSource: null,

  connect: (sellerId?: number) => {
    const existing = get().eventSource;
    if (existing) existing.close();

    const url = `/api/events${sellerId ? `?seller_id=${sellerId}` : ''}`;
    const es = new EventSource(url);

    es.onopen = () => set({ connected: true });

    es.onerror = () => {
      set({ connected: false });
      // Auto-reconnect after 5s
      setTimeout(() => {
        if (!get().connected) get().connect(sellerId);
      }, 5000);
    };

    es.addEventListener('connected', () => set({ connected: true }));

    // Generic message handler that dispatches to subscribers
    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        const listeners = get().listeners.get('message');
        listeners?.forEach((cb) => cb(data));
      } catch { /* ignore */ }
    };

    set({ eventSource: es });
  },

  disconnect: () => {
    const es = get().eventSource;
    if (es) es.close();
    set({ eventSource: null, connected: false });
  },

  subscribe: (event: string, callback: EventCallback) => {
    const { listeners, eventSource } = get();

    if (!listeners.has(event)) {
      listeners.set(event, new Set());

      // Register SSE event listener
      eventSource?.addEventListener(event, (e: Event) => {
        try {
          const data = JSON.parse((e as MessageEvent).data);
          const cbs = get().listeners.get(event);
          cbs?.forEach((cb) => cb(data));
        } catch { /* ignore */ }
      });
    }

    listeners.get(event)!.add(callback);
    set({ listeners: new Map(listeners) });

    // Return unsubscribe function
    return () => {
      const current = get().listeners.get(event);
      current?.delete(callback);
    };
  },
}));
